import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  CopyButton,
  BrandedExportActions,
  EmptyHint,
  FullscreenButton,
  HistoryPanel,
  ListInput,
  Panel,
  ResetButton,
  SoundToggle,
  usePersistentState,
  useSfx,
} from "@/components/tools/ToolKit";
import { DEFAULT_CLASS_LIST, dedupe, parseList, randomInt, shuffle } from "@/lib/random-engine";

/* ---------------- Random Number Generator ---------------- */
export function NumberGenerator() {
  const [min, setMin] = useState(1);
  const [max, setMax] = useState(100);
  const [howMany, setHowMany] = useState(1);
  const [unique, setUnique] = useState(true);
  const [results, setResults] = useState<number[]>([]);
  const [display, setDisplay] = useState<number[]>([]);
  const [locked, setLocked] = useState(0);
  const [rolling, setRolling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [soundOn, setSoundOn] = usePersistentState("pickamini:numbers:sound", true);
  const sfx = useSfx(soundOn);
  const lockedRef = useRef(0);
  const timers = useRef<number[]>([]);

  const clearTimers = () => {
    timers.current.forEach((id) => {
      window.clearTimeout(id);
      window.clearInterval(id);
    });
    timers.current = [];
  };

  useEffect(() => clearTimers, []);

  const generate = () => {
    if (rolling) return;
    if (!Number.isFinite(min) || !Number.isFinite(max) || max < min || howMany < 1) {
      setError("Please enter a valid number range.");
      return;
    }
    const span = max - min + 1;
    if (unique && howMany > span) {
      setError(`Only ${span} unique numbers available in this range.`);
      return;
    }
    setError(null);

    const final = unique
      ? shuffle(Array.from({ length: span }, (_, i) => min + i)).slice(0, howMany)
      : Array.from({ length: howMany }, () => min + randomInt(span));

    clearTimers();
    lockedRef.current = 0;
    setLocked(0);
    setResults([]);
    setDisplay(final.map(() => min + randomInt(span)));
    setRolling(true);
    sfx.whoosh();

    const scramble = window.setInterval(() => {
      setDisplay((prev) =>
        prev.map((v, i) => (i < lockedRef.current ? final[i]! : min + randomInt(span))),
      );
      sfx.tick(0.35);
    }, 55);
    timers.current.push(scramble);

    // Slots settle one after another; stagger shrinks so long batches stay snappy.
    const stagger = Math.min(180, 900 / final.length);
    final.forEach((_, i) => {
      timers.current.push(
        window.setTimeout(
          () => {
            lockedRef.current = i + 1;
            setLocked(i + 1);
            sfx.click();
          },
          600 + i * stagger,
        ),
      );
    });

    timers.current.push(
      window.setTimeout(
        () => {
          clearTimers();
          setDisplay(final);
          setResults(final);
          setRolling(false);
          sfx.fanfare();
        },
        600 + final.length * stagger + 150,
      ),
    );
  };

  const reset = () => {
    clearTimers();
    lockedRef.current = 0;
    setLocked(0);
    setRolling(false);
    setDisplay([]);
    setResults([]);
    setError(null);
  };

  const shown = display.length ? display : results;

  return (
    <div className="grid gap-5 lg:grid-cols-[340px_1fr]">
      <Panel title="Settings" className="no-print">
        <div className="grid grid-cols-2 gap-3">
          <label className="text-sm font-medium">
            Minimum
            <Input
              type="number"
              value={min}
              onChange={(e) => setMin(Number(e.target.value))}
              className="mt-1"
            />
          </label>
          <label className="text-sm font-medium">
            Maximum
            <Input
              type="number"
              value={max}
              onChange={(e) => setMax(Number(e.target.value))}
              className="mt-1"
            />
          </label>
          <label className="col-span-2 text-sm font-medium">
            How many results
            <Input
              type="number"
              min={1}
              value={howMany}
              onChange={(e) => setHowMany(Number(e.target.value))}
              className="mt-1"
            />
          </label>
        </div>
        <div className="mt-4 flex items-center justify-between rounded-lg bg-secondary/70 px-3 py-2">
          <span className="text-sm font-medium">Unique results</span>
          <Switch checked={unique} onCheckedChange={setUnique} />
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button onClick={generate} disabled={rolling}>
            {rolling ? "Generating…" : "Generate"}
          </Button>
          <SoundToggle on={soundOn} onToggle={() => setSoundOn(!soundOn)} />
          <ResetButton onClick={reset} />
        </div>
        {error ? (
          <div className="mt-3">
            <EmptyHint>{error}</EmptyHint>
          </div>
        ) : null}
      </Panel>
      <div className="surface-card relative grid min-h-[300px] place-items-center overflow-hidden bg-hero-gradient p-8 text-primary-foreground">
        {rolling ? (
          <span
            className="animate-glow pointer-events-none absolute inset-8 rounded-[2.5rem] bg-white/20 blur-2xl"
            aria-hidden="true"
          />
        ) : null}
        {shown.length ? (
          <div className="relative text-center">
            <div className="flex flex-wrap justify-center gap-3">
              {shown.map((n, i) => {
                const settled = i < locked;
                return (
                  <span key={i} className="relative grid place-items-center">
                    {settled && rolling ? (
                      <span
                        className="animate-number-shockwave absolute inset-0 rounded-2xl bg-white/40"
                        aria-hidden="true"
                      />
                    ) : null}
                    <span
                      className={`relative rounded-2xl px-6 py-4 font-display text-4xl font-bold tabular-nums sm:text-5xl ${
                        settled
                          ? "animate-number-lock bg-white/25 shadow-lift"
                          : "animate-number-roll bg-white/10 text-primary-foreground/80"
                      }`}
                    >
                      {n}
                    </span>
                  </span>
                );
              })}
            </div>
            <div className="mt-6 min-h-9 no-print">
              {results.length ? (
                <div className="flex flex-wrap justify-center gap-2">
                  <CopyButton text={results.join(", ")} label="Copy results" />
                  <BrandedExportActions
                    title="Random Number Results"
                    content={results.join(", ")}
                    filename="picamini-number-results.png"
                  />
                </div>
              ) : null}
            </div>
          </div>
        ) : (
          <p className="opacity-80">Set a range and press Generate.</p>
        )}
      </div>
    </div>
  );
}

/* ---------------- Dice Roller ---------------- */
export { DiceRoller } from "./DiceRoller";

/* ---------------- Coin Flip ---------------- */
export { CoinFlip } from "./CoinFlip";

/* ---------------- Classroom Timer ---------------- */
const PRESETS = [1, 2, 5, 10, 15, 30];

export function ClassroomTimer() {
  const [total, setTotal] = useState(300);
  const [remaining, setRemaining] = useState(300);
  const [customMinutes, setCustomMinutes] = useState(5);
  const [customSeconds, setCustomSeconds] = useState(0);
  const [running, setRunning] = useState(false);
  const [soundOn, setSoundOn] = usePersistentState("pickamini:timer:sound", true);
  const stageRef = useRef<HTMLDivElement>(null);
  const sfx = useSfx(soundOn);
  const deadlineRef = useRef<number>(0);

  useEffect(() => {
    if (!running) return;
    deadlineRef.current = Date.now() + remaining * 1000;
    const iv = window.setInterval(() => {
      const left = Math.max(0, Math.round((deadlineRef.current - Date.now()) / 1000));
      setRemaining(left);
      if (left === 0) {
        setRunning(false);
        window.clearInterval(iv);
      }
    }, 250);
    return () => window.clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running]);

  useEffect(() => {
    if (remaining === 0 && total > 0) sfx.beep(true);
    else if (remaining > 0 && remaining <= 10) sfx.beep(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remaining, total]);

  const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
  const ss = String(remaining % 60).padStart(2, "0");
  const done = remaining === 0;
  const urgent = remaining > 0 && remaining <= 10;
  const progress = total ? ((total - remaining) / total) * 100 : 0;

  return (
    <div className="grid gap-5 lg:grid-cols-[320px_1fr]">
      <Panel title="Timer setup" className="no-print">
        <p className="mb-2 text-sm font-medium">Presets</p>
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((m) => (
            <Button
              key={m}
              size="sm"
              variant={total === m * 60 ? "default" : "outline"}
              onClick={() => {
                setRunning(false);
                setTotal(m * 60);
                setRemaining(m * 60);
                setCustomMinutes(m);
                setCustomSeconds(0);
              }}
            >
              {m} min
            </Button>
          ))}
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <label className="text-sm font-medium">
            Custom minutes
            <Input
              type="number"
              min={0}
              value={customMinutes}
              className="mt-1"
              onChange={(e) => {
                const minutes = Math.max(0, Number(e.target.value) || 0);
                const secs = minutes * 60 + customSeconds;
                setCustomMinutes(minutes);
                setRunning(false);
                setTotal(secs);
                setRemaining(secs);
              }}
            />
          </label>
          <label className="text-sm font-medium">
            Custom seconds
            <Input
              type="number"
              min={0}
              max={59}
              value={customSeconds}
              className="mt-1"
              onChange={(e) => {
                const seconds = Math.min(59, Math.max(0, Number(e.target.value) || 0));
                const secs = customMinutes * 60 + seconds;
                setCustomSeconds(seconds);
                setRunning(false);
                setTotal(secs);
                setRemaining(secs);
              }}
            />
          </label>
        </div>
        <div className="mt-4 flex gap-2">
          <SoundToggle on={soundOn} onToggle={() => setSoundOn(!soundOn)} />
          <FullscreenButton targetRef={stageRef} />
        </div>
      </Panel>

      <div
        ref={stageRef}
        className={`surface-card relative flex flex-col items-center justify-center gap-8 p-10 ${done ? "bg-accent-gradient text-accent-foreground" : "bg-hero-gradient text-primary-foreground"}`}
      >
        <div className="absolute top-4 right-4 flex items-center gap-2">
          <FullscreenButton targetRef={stageRef} />
        </div>

        <p
          className={`font-display text-7xl font-bold tabular-nums sm:text-8xl ${urgent ? "animate-soft-pulse" : ""} ${done ? "animate-tick-flash" : ""}`}
          aria-live="polite"
        >
          {done ? "Time's up!" : `${mm}:${ss}`}
        </p>
        <div className="h-2 w-full max-w-lg overflow-hidden rounded-full bg-white/25">
          <div
            className="h-full rounded-full bg-white transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="flex flex-wrap justify-center gap-3">
          <Button
            size="lg"
            variant="secondary"
            className="h-13 px-8 text-base font-semibold"
            onClick={() => setRunning((r) => !r)}
            disabled={remaining === 0}
          >
            {running ? "Pause" : "Start"}
          </Button>
          <Button
            size="lg"
            variant="outline"
            className="h-13 px-8 text-base"
            onClick={() => {
              setRunning(false);
              setRemaining(total);
            }}
          >
            Reset
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Seating Chart ---------------- */
export { SeatingChart } from "./SeatingChart";
